/**
 * "Saltar a los resultados" — the second skip target of the console.
 *
 * QA 2026-09-15 **UX-568**: reaching a row costs 24–31 Tabs on a clean list and **62** on
 * /access once a search has been typed, because the tab order is skip link → 13 sidebar links →
 * header → toolbar → every filter and chip → «Exportar CSV» → the pager → *then* the grid. The
 * single «Saltar al contenido» link lands on `<main>`, i.e. before all of it.
 *
 * A screen that renders a `DataTable` therefore registers here and the shell renders a second
 * skip link, right after the first one, that focuses the list container directly: 62 Tabs → 3.
 * A route with no list (the dashboard, an editor) registers nothing and the link is not there
 * to lie about a target that does not exist.
 *
 * QA 2026-09-15 **UX-576**: /users, /sessions and /review render lists too — card lists, not
 * grids — and had no such link, so they were still 18 and 19 Tabs to the first card. They
 * register through {@link useResultsAnchor} like any grid and name their list container with
 * {@link resultsAnchorProps}, so the landing is announced instead of being a bare `<div>`.
 */
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';

/** Id of the list container the link jumps to. */
export const RESULTS_ANCHOR_ID = 'list-results';

type Register = (delta: 1 | -1) => void;

const ResultsAnchorContext = createContext<Register | null>(null);

/** Announces a list on screen for as long as the component is mounted. */
export function useResultsAnchor(): void {
  const register = useContext(ResultsAnchorContext);
  useEffect(() => {
    if (!register) return;
    register(1);
    return () => register(-1);
  }, [register]);
}

/**
 * Props for the element the skip link lands on: focusable, identified, and — because a skip
 * link that lands on something unnamed tells a screen-reader user nothing (UX-576) — named.
 */
export function resultsAnchorProps(label: string): {
  id: string;
  tabIndex: -1;
  'aria-label': string;
} {
  return { id: RESULTS_ANCHOR_ID, tabIndex: -1, 'aria-label': label };
}

export function ResultsAnchorProvider({
  children,
}: {
  children: (hasResults: boolean) => ReactNode;
}) {
  // A count rather than a flag: a route change mounts the next list before unmounting the last.
  const [count, setCount] = useState(0);
  const register = useCallback<Register>((delta) => setCount((n) => Math.max(0, n + delta)), []);
  return (
    <ResultsAnchorContext.Provider value={register}>
      {children(count > 0)}
    </ResultsAnchorContext.Provider>
  );
}
